/* House of 3D - the Spiral Tree Pot printing, as a loop. Replaces the uploaded print video on
   product pages: the real GLB rises out of the bed behind a clipping plane while a nozzle
   orbits the current layer, holds finished for a beat, then starts again. Pauses off-screen. */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

document.querySelectorAll('[data-h3d-pot-print]').forEach(boot);

function boot(root) {
  const canvas = root.querySelector('canvas');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  let renderer;
  try { renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' }); }
  catch (e) { root.classList.add('no-gl'); return; }
  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 0);
  renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.localClippingEnabled = true;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(28, 1, 1, 5000);
  const pot = new THREE.Group(); scene.add(pot);

  scene.add(new THREE.HemisphereLight(0xf2f2f2, 0x0d0d24, 0.9));
  const key = new THREE.DirectionalLight(0xfff6e0, 2.0); key.position.set(260, 520, 320); key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  const sc = key.shadow.camera; sc.left = -260; sc.right = 260; sc.top = 320; sc.bottom = -260; sc.near = 50; sc.far = 1600; key.shadow.bias = -0.0005;
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x0000f2, 0.8); rim.position.set(-360, 200, -300); scene.add(rim);
  const bed = new THREE.Mesh(new THREE.PlaneGeometry(1400, 1400), new THREE.ShadowMaterial({ opacity: 0.3 }));
  bed.rotation.x = -Math.PI / 2; bed.position.y = -0.5; bed.receiveShadow = true; scene.add(bed);

  // the layer being laid: everything above the plane is not printed yet
  const clip = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);

  // nozzle: cone + barrel + heater block, orbits the current layer's rim
  const nozzle = new THREE.Group();
  const nozMat = new THREE.MeshStandardMaterial({ color: 0x9aa3b3, roughness: 0.4, metalness: 0.6 });
  const tip = new THREE.Mesh(new THREE.ConeGeometry(3.2, 7, 20), nozMat); tip.rotation.x = Math.PI; tip.position.y = 3.5;
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(4.5, 4.5, 13, 20), nozMat); barrel.position.y = 13.5;
  const block = new THREE.Mesh(new THREE.BoxGeometry(20, 10, 14), new THREE.MeshStandardMaterial({ color: 0x19192f, roughness: 0.7 })); block.position.y = 25;
  nozzle.add(tip, barrel, block); nozzle.visible = false; scene.add(nozzle);

  let ready = false, H = 1, profile = null, bins = 64;
  new GLTFLoader().load(root.dataset.src, (gltf) => {
    const box = new THREE.Box3().setFromObject(gltf.scene);
    const size = box.getSize(new THREE.Vector3()); H = size.y;
    gltf.scene.position.set(-(box.min.x + box.max.x) / 2, -box.min.y, -(box.min.z + box.max.z) / 2);
    // max radius per height band, so the nozzle rides the outside of the print
    profile = new Float32Array(bins);
    gltf.scene.traverse((o) => {
      if (!o.isMesh) return;
      o.castShadow = true; o.receiveShadow = true;
      const m = o.material && o.material.isMeshStandardMaterial ? o.material : new THREE.MeshStandardMaterial({ color: 0x0000f2, roughness: 0.6 });
      m.clippingPlanes = [clip]; m.clipShadows = true; m.side = THREE.DoubleSide; o.material = m;
      const p = o.geometry.attributes.position, cx = (box.min.x + box.max.x) / 2, cz = (box.min.z + box.max.z) / 2;
      const v = new THREE.Vector3();
      for (let i = 0; i < p.count; i += 3) {
        v.fromBufferAttribute(p, i); o.localToWorld(v);
        const b = Math.min(bins - 1, Math.max(0, Math.floor((v.y - box.min.y) / H * bins)));
        const r = Math.hypot(v.x - cx, v.z - cz);
        if (r > profile[b]) profile[b] = r;
      }
    });
    pot.add(gltf.scene);
    ready = true;
  }, undefined, () => { root.classList.add('no-gl'); });

  let w = 0, h = 0;
  function resize() {
    const r = root.getBoundingClientRect();
    const nw = Math.max(1, Math.round(r.width)), nh = Math.max(1, Math.round(r.height));
    if (nw === w && nh === h) return;
    w = nw; h = nh; renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
  }

  // loop timing (seconds)
  const BUILD = 9, HOLD = 3, RESET = 0.6, TOTAL = BUILD + HOLD + RESET;
  const easeInOut = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  let t0 = null, visible = true, raf = 0;

  function frame(now) {
    raf = requestAnimationFrame(frame);
    if (!visible) return;
    resize();
    if (!ready) return;
    if (t0 === null) t0 = now;
    const t = ((now - t0) / 1000) % TOTAL;
    let f, opacity = 1;
    if (reduce.matches) f = 1;
    else if (t < BUILD) f = easeInOut(t / BUILD);
    else if (t < BUILD + HOLD) f = 1;
    else { f = 1; opacity = 1 - (t - BUILD - HOLD) / RESET; }
    const y = f * H;
    clip.constant = y + 0.01;
    pot.rotation.y = reduce.matches ? 0.6 : (now / 1000) * 0.18;
    root.style.setProperty('--h3d-fade', opacity.toFixed(3));

    const laying = !reduce.matches && t < BUILD;
    nozzle.visible = laying;
    if (laying && profile) {
      const b = Math.min(bins - 1, Math.floor(f * bins));
      const r = (profile[b] || profile[Math.max(0, b - 1)] || 20) + 1.5;
      const a = (now / 1000) * 5.2;                       // one orbit every ~1.2 s
      nozzle.position.set(Math.cos(a) * r, y + 0.4, Math.sin(a) * r);
    }

    const tall = h > w;
    const d = tall ? 760 : 610;
    camera.position.set(d, 300 + 0.42 * H, d);
    camera.lookAt(0, H * 0.5, 0);
    renderer.render(scene, camera);
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver((es) => { visible = es.some((e) => e.isIntersecting); }, { rootMargin: '120px' }).observe(root);
  }
  addEventListener('resize', () => { w = 0; }, { passive: true });
  root.classList.add('is-gl');
  raf = requestAnimationFrame(frame);
}
